import React, { useEffect, useMemo, useState } from "react";
import { api, fmtUSD, fmtNumber } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, CartesianGrid,
} from "recharts";
import {
  Sparkles, Zap, Target, Award, Play, FlameKindling, Activity,
  TrendingDown, TrendingUp,
} from "lucide-react";

const PROVIDER_COLORS = {
  OpenAI:    "#10a37f",
  Anthropic: "#d97706",
  Google:    "#1a73e8",
  DeepSeek:  "#6d28d9",
};

const BADGE_STYLES = {
  best_price:  { label: "Best price",  bg: "#dcfce7", color: "#15803d", icon: Award },
  recommended: { label: "Recommended", bg: "#dbeafe", color: "#1d4ed8", icon: Sparkles },
  baseline:    { label: "Baseline",    bg: "#f3f4f6", color: "#6b7280", icon: Target },
};

function ProviderDot({ provider }) {
  return (
    <span className="inline-flex items-center justify-center w-5 h-5 rounded-md text-[10px] font-bold text-white shrink-0"
          style={{ background: PROVIDER_COLORS[provider] || "#52525B" }}>
      {provider?.[0] || "?"}
    </span>
  );
}

function QualityBar({ value }) {
  const pct = Math.round((value || 0) * 100);
  const color = value >= 0.90 ? "#15803d" : value >= 0.78 ? "#1d4ed8" : "#9a3412";
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-12 h-1 bg-[hsl(var(--cs-tint))] rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="cs-mono text-[11px] tabular-nums" style={{ color }}>{value?.toFixed(2)}</span>
    </div>
  );
}

export default function TMBestModelsPanel() {
  const [catalog, setCatalog] = useState(null);
  const [prompt, setPrompt] = useState(
    "Write a 5-line root-cause summary for a BigQuery slot exhaustion incident affecting the prod-data-platform ETL pipeline."
  );
  const [taskId, setTaskId] = useState("rca_summary");
  const [baselineId, setBaselineId] = useState("gpt-4o-mini");
  const [monthlyVolume, setMonthlyVolume] = useState(2400);
  const [result, setResult] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [sortField, setSortField] = useState("total_cost_usd");
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    Promise.all([
      api.tm.bestModels.catalog(),
      api.tm.bestModels.heatmap(10),
      api.tm.bestModels.accuracy(),
    ]).then(([c, h, a]) => {
      setCatalog(c); setHeatmap(h); setAccuracy(a);
    }).catch(console.error);
  }, []);

  // Auto-run analyze whenever the inputs settle (debounced)
  useEffect(() => {
    if (!catalog) return;
    const t = setTimeout(() => runAnalyze(), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, baselineId, monthlyVolume, catalog]);

  const runAnalyze = async () => {
    if (!prompt || !prompt.trim()) return;
    setAnalyzing(true);
    try {
      const r = await api.tm.bestModels.analyze({
        prompt,
        task_id: taskId,
        baseline_model_id: baselineId,
        monthly_volume: monthlyVolume,
      });
      setResult(r);
    } catch (e) {
      console.error(e);
    } finally {
      setAnalyzing(false);
    }
  };

  const sortedRows = useMemo(() => {
    if (!result?.rows) return [];
    const rows = [...result.rows];
    rows.sort((a, b) => {
      const av = a[sortField] ?? 0;
      const bv = b[sortField] ?? 0;
      const dir = sortDir === "asc" ? 1 : -1;
      if (typeof av === "string") return av.localeCompare(bv) * dir;
      return (av - bv) * dir;
    });
    return rows;
  }, [result, sortField, sortDir]);

  const flipSort = (field) => {
    if (field === sortField) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir(field === "quality" ? "desc" : "asc"); }
  };

  if (!catalog) {
    return <div className="cs-card p-10 text-center text-muted-foreground">Loading TIPE catalog…</div>;
  }

  return (
    <div className="space-y-6" data-testid="tm-best-models-panel">
      {/* ── Analyzer form ───────────────────────────────────────────────── */}
      <div className="cs-card p-6" data-testid="bm-analyzer-card">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div>
            <h3 className="cs-heading text-base font-semibold tracking-tight flex items-center gap-2">
              <FlameKindling className="w-4 h-4 text-[#00579e]" /> TIPE Analyzer
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Paste a prompt → see predicted output tokens & total cost across {catalog.models.length} frontier models.
            </p>
          </div>
          {/* Task chips */}
          <div className="flex flex-wrap gap-1.5" data-testid="bm-task-chips">
            {catalog.tasks.map(t => (
              <button
                key={t.id}
                onClick={() => setTaskId(t.id)}
                data-testid={`bm-task-${t.id}`}
                className={`px-3 h-7 rounded-full text-[11px] font-medium transition-colors border ${
                  taskId === t.id
                    ? "bg-[#00579e] text-white border-[#00579e]"
                    : "bg-white text-foreground border-border hover:border-[#00579e]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <Textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onBlur={runAnalyze}
          rows={4}
          placeholder="Paste any prompt — TIPE will predict cost across all models."
          data-testid="bm-prompt-input"
          className="rounded-lg cs-mono text-xs"
        />
        <div className="flex justify-between items-center text-[10px] text-muted-foreground mt-1 cs-mono">
          <span>{prompt.length} chars · ≈{Math.max(1, Math.ceil(prompt.length / 4))} input tokens</span>
          <button onClick={() => setPrompt("")} className="hover:text-[#00579e]" data-testid="bm-clear">Clear</button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end mt-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5 block">Baseline model</label>
            <Select value={baselineId} onValueChange={setBaselineId}>
              <SelectTrigger className="rounded-lg h-9" data-testid="bm-baseline-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {catalog.models.map(m => (
                  <SelectItem key={m.id} value={m.id} className="text-xs">
                    {m.name} · ${m.input_pm.toFixed(2)}/${m.output_pm.toFixed(2)} per M
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5 block">
              Monthly volume · <span className="cs-mono text-foreground font-semibold">{fmtNumber(monthlyVolume)}</span> / month
            </label>
            <input
              type="range" min={100} max={50_000} step={100}
              value={monthlyVolume}
              onChange={(e) => setMonthlyVolume(parseInt(e.target.value))}
              data-testid="bm-volume-slider"
              className="w-full accent-[#00579e]"
            />
          </div>
          <Button
            onClick={runAnalyze}
            disabled={analyzing || !prompt.trim()}
            data-testid="bm-run-analysis"
            className="bg-[#00579e] hover:bg-[#004580] text-white rounded-full h-9 px-5"
          >
            {analyzing ? "Analyzing…" : <><Play className="w-3.5 h-3.5 mr-1.5" /> Run TIPE Analysis</>}
          </Button>
        </div>
      </div>

      {result && (
        <>
          {/* ── Cost summary + chart ────────────────────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="cs-card p-6" data-testid="bm-cost-summary">
              <h3 className="cs-heading text-base font-semibold tracking-tight mb-4">Cost summary · {fmtNumber(monthlyVolume)} runs / month</h3>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Baseline / mo</div>
                  <div className="cs-kpi-value text-2xl text-foreground mt-1" data-testid="bm-baseline-monthly">{fmtUSD(result.cost_summary.baseline_monthly_usd)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Recommended / mo</div>
                  <div className="cs-kpi-value text-2xl text-[#1d4ed8] mt-1" data-testid="bm-recommended-monthly">{fmtUSD(result.cost_summary.recommended_monthly_usd)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Savings / mo</div>
                  <div className="cs-kpi-value text-2xl text-[#15803d] mt-1" data-testid="bm-savings-monthly">{fmtUSD(result.cost_summary.savings_monthly_usd)}</div>
                  <div className="text-[11px] text-[#15803d] mt-0.5 cs-mono">-{result.cost_summary.savings_pct}%</div>
                </div>
              </div>
              <div className="mt-4 p-3 rounded-lg bg-[hsl(var(--cs-tint))] border border-border text-xs leading-relaxed text-foreground">
                <span className="font-semibold">{result.task.label}</span> task · baseline{" "}
                <span className="cs-mono text-[#00579e]">{result.baseline_model_id}</span> →{" "}
                recommended <span className="cs-mono text-[#1d4ed8]">{result.recommended_model_id}</span>.
              </div>
            </div>

            <div className="cs-card p-6" data-testid="bm-cost-chart">
              <h3 className="cs-heading text-base font-semibold tracking-tight mb-1">Total cost per call</h3>
              <p className="text-xs text-muted-foreground mb-2">Sorted cheapest → most expensive</p>
              <div className="h-56">
                <ResponsiveContainer>
                  <BarChart data={result.chart} margin={{ left: 10, right: 12, top: 8, bottom: 28 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 9, fontFamily: "IBM Plex Mono" }} stroke="#6b7280"
                           interval={0} angle={-35} textAnchor="end" height={50} />
                    <YAxis tick={{ fontSize: 10 }} stroke="#6b7280"
                           tickFormatter={(v) => v < 0.001 ? `$${(v*1000).toFixed(2)}m` : `$${v.toFixed(4)}`} />
                    <Tooltip contentStyle={{ borderRadius: 6, fontSize: 12 }}
                             formatter={(v) => `$${Number(v).toFixed(6)}`} />
                    <Bar dataKey="total_cost_usd" radius={[3, 3, 0, 0]}>
                      {result.chart.map((d, i) => (
                        <Cell key={i} fill={d.model_id === result.recommended_model_id
                          ? "#1d4ed8"
                          : d.model_id === result.baseline_model_id
                          ? "#9ca3af" : "#00579e"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* ── Per-model table ─────────────────────────────────────────── */}
          <div className="cs-card overflow-hidden" data-testid="bm-results-table-card">
            <div className="px-6 py-4 border-b border-border">
              <h3 className="cs-heading text-base font-semibold tracking-tight">Predicted output & cost across {result.rows.length} models</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Confidence band = ±{result.rows[0]?.confidence_band_pct || 15}% on predicted output tokens · Δ vs baseline reflects per-call cost.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
                  <tr className="text-left">
                    {[
                      { id: "name",                       l: "Model"            },
                      { id: "input_tokens",               l: "Input"            },
                      { id: "predicted_output_tokens",    l: "Predicted Output" },
                      { id: "confidence_band",            l: "Confidence Band"  },
                      { id: "total_cost_usd",             l: "Total Cost"       },
                      { id: "delta_vs_baseline_pct",      l: "Δ vs Baseline"    },
                      { id: "quality",                    l: "Quality"          },
                      { id: "badge",                      l: "Badge"            },
                    ].map(col => (
                      <th key={col.id}
                          onClick={() => flipSort(col.id)}
                          className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground cursor-pointer hover:text-foreground select-none"
                          data-testid={`bm-th-${col.id}`}>
                        {col.l}
                        {sortField === col.id && <span className="ml-1">{sortDir === "asc" ? "↑" : "↓"}</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map(row => {
                    const badge = BADGE_STYLES[row.badge];
                    const isBaseline = row.is_baseline;
                    return (
                      <tr key={row.model_id}
                          data-testid={`bm-row-${row.model_id}`}
                          className={`cs-row-hover border-b border-border last:border-0 ${isBaseline ? "bg-[hsl(var(--cs-tint))]/30" : ""}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <ProviderDot provider={row.provider} />
                            <span className="cs-mono text-xs font-medium text-foreground">{row.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 cs-mono text-xs text-muted-foreground">{fmtNumber(row.input_tokens)}</td>
                        <td className="px-4 py-3 cs-mono text-xs">{fmtNumber(row.predicted_output_tokens)}</td>
                        <td className="px-4 py-3">
                          <span className="cs-pill bg-[#f3f4f6] text-[#374151] cs-mono text-[10px]">
                            {row.confidence_band[0]} – {row.confidence_band[1]} · {row.confidence_band_pct}%
                          </span>
                        </td>
                        <td className="px-4 py-3 cs-mono text-xs font-semibold">${row.total_cost_usd.toFixed(6)}</td>
                        <td className="px-4 py-3">
                          {row.is_baseline ? (
                            <span className="text-muted-foreground text-xs">—</span>
                          ) : row.delta_vs_baseline_pct < 0 ? (
                            <span className="cs-mono text-xs font-semibold text-[#15803d] flex items-center gap-1">
                              <TrendingDown className="w-3 h-3" /> {row.delta_vs_baseline_pct}%
                            </span>
                          ) : (
                            <span className="cs-mono text-xs font-semibold text-[#b91c1c] flex items-center gap-1">
                              <TrendingUp className="w-3 h-3" /> +{row.delta_vs_baseline_pct}%
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3"><QualityBar value={row.quality} /></td>
                        <td className="px-4 py-3">
                          {badge ? (
                            <span className="cs-pill" style={{ background: badge.bg, color: badge.color }} data-testid={`bm-badge-${row.model_id}`}>
                              <badge.icon className="w-3 h-3" /> {badge.label}
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-3 border-t border-border text-[10px] uppercase tracking-[0.14em] text-muted-foreground flex justify-between">
              <span>{result.rows.length} models · sorted by {sortField.replace(/_/g, " ")}</span>
              <span>Cells use a calibrated tokenizer approximation</span>
            </div>
          </div>
        </>
      )}

      {/* ── Bottom strip: Savings heatmap + Prediction accuracy ─────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="cs-card p-6" data-testid="bm-savings-heatmap">
          <div className="flex items-start gap-2 mb-1">
            <Zap className="w-4 h-4 text-[#15803d] mt-0.5" />
            <div>
              <h3 className="cs-heading text-base font-semibold tracking-tight">Savings Opportunity Heatmap</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Top 10 model→model migrations ranked by projected monthly savings.</p>
            </div>
          </div>
          <ol className="mt-4 space-y-3">
            {(heatmap?.rows || []).map(row => (
              <li key={row.rank} className="flex gap-3 pb-3 border-b border-border last:border-0 last:pb-0"
                  data-testid={`bm-heatmap-row-${row.rank}`}>
                <div className="cs-mono text-xs text-muted-foreground w-5 pt-0.5">{row.rank}.</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 text-sm cs-mono font-medium">
                    <span>{row.from_model_id}</span>
                    <span className="text-[#00579e]">→</span>
                    <span className="text-[#15803d]">{row.to_model_id}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{row.summary}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="cs-mono text-sm font-semibold text-[#15803d]">{fmtUSD(row.monthly_savings_usd)}</div>
                  <div className="text-[10px] text-muted-foreground cs-mono">+{row.savings_pct_per_call}% per call</div>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="cs-card p-6" data-testid="bm-prediction-accuracy">
          <div className="flex items-start gap-2 mb-1">
            <Activity className="w-4 h-4 text-[#1d4ed8] mt-0.5" />
            <div>
              <h3 className="cs-heading text-base font-semibold tracking-tight">Prediction Accuracy</h3>
              <p className="text-xs text-muted-foreground mt-0.5">How close TIPE's predictions are to actual tokens (lower = better).</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="border border-border rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">P50 error</div>
              <div className="cs-kpi-value text-xl text-[#92400e] mt-1">±{accuracy?.p50_error_pct?.toFixed(2)}%</div>
            </div>
            <div className="border border-border rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">P80 error</div>
              <div className="cs-kpi-value text-xl text-[#92400e] mt-1">±{accuracy?.p80_error_pct?.toFixed(2)}%</div>
            </div>
            <div className="border border-border rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">P95 error</div>
              <div className="cs-kpi-value text-xl text-[#92400e] mt-1">±{accuracy?.p95_error_pct?.toFixed(2)}%</div>
            </div>
          </div>
          <div className="mt-4 max-h-[260px] overflow-y-auto pr-1">
            {(accuracy?.per_model || []).map(m => (
              <div key={m.model_id} className="flex items-center justify-between text-xs py-1.5 border-b border-border last:border-0"
                   data-testid={`bm-accuracy-${m.model_id}`}>
                <span className="cs-mono text-foreground">{m.model_id}</span>
                <span className="cs-mono text-muted-foreground">P80 ±{m.p80_error_pct?.toFixed(2)}% · {m.samples} samples</span>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
            Target: ±{accuracy?.target_p80_pct}% P80, ±{accuracy?.target_p95_pct}% P95 · {accuracy?.observed_samples} observed samples
          </div>
        </div>
      </div>
    </div>
  );
}
